import React, { useCallback, useRef, useState } from 'react';
import { App, Dropdown } from 'antd';
import type { MenuProps, DropdownProps } from 'antd';
import { rteSelectPopupContainer } from '../utils/selectPlacements';
import {
  filterFooterFiles,
  FOOTER_IMAGE_SIZE_ERROR,
  FOOTER_TOTAL_SIZE_ERROR,
  FOOTER_UNSUPPORTED_TYPE_ERROR,
} from './footerFileLimits';

// 上传二级菜单自适应翻转（与 AgentDropdown 同款）：placement=bottomLeft 默认向下；下方空间不足时
// 靠 adjustY 翻到按钮上方、始终锚定按钮。shiftY 必须 false——否则下方放不下时 rc-trigger 会平移
// 整个菜单进视口而脱离按钮。翻转方向算得对还需配合 getPopupContainer=document.body（逃出消费端
// 滚动祖先，用真实视口算可视空间），详见 selectPlacements.ts。
const UPLOAD_DROPDOWN_ADJUST = {
  adjustX: 1,
  adjustY: 1,
  shiftX: false,
  shiftY: false,
} as unknown as DropdownProps['autoAdjustOverflow'];

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  onAssetLibraryClick?: () => void;
};

/** footer 上传触发按钮：二级菜单支持上传文件/图片和选择资产库文件。
 *  文件 chip 列表由 FooterFilesList 在编辑区上方独立渲染（对齐 V1 位置）。 */
export const UploadButton: React.FC<Props> = React.memo(({ files, onChange, disabled, onAssetLibraryClick }) => {
  const { message } = App.useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onPick = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (picked.length === 0) return;
      const { accepted, imageOversize, totalExceeded, unsupportedType } =
        await filterFooterFiles(files, picked);
      if (unsupportedType) message.error(FOOTER_UNSUPPORTED_TYPE_ERROR);
      if (imageOversize) message.error(FOOTER_IMAGE_SIZE_ERROR);
      if (totalExceeded) message.error(FOOTER_TOTAL_SIZE_ERROR);
      if (accepted.length > 0) onChange([...files, ...accepted]);
    },
    [files, onChange, message],
  );

  const handleMenuClick: MenuProps['onClick'] = useCallback(
    ({ key }: { key: string }) => {
      if (disabled) return;
      setDropdownOpen(false);
      if (key === 'upload') {
        inputRef.current?.click();
      } else if (key === 'asset-library') {
        onAssetLibraryClick?.();
      }
    },
    [disabled, onAssetLibraryClick],
  );

  const menuItems: MenuProps['items'] = [
    {
      key: 'upload',
      label: '上传文件或图片',
      icon: <i className="iconfont icon-shangchuanwenjianhuotupian" style={{ fontSize: '16px',width:'16px',height:'16px' }} />,
    },
    {
      key: 'asset-library',
      label: '选择资产库文件',
      icon: <i className="iconfont icon-xuanzezichankuwenjian" style={{ fontSize: '16px',width:'16px',height:'16px' }} />,
    },
  ];

  return (
    <div className="rte-footer-upload">
      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={UPLOAD_DROPDOWN_ADJUST}
        getPopupContainer={rteSelectPopupContainer}
        disabled={disabled}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        rootClassName="rte-upload-dropdown-menu"
      >
        <div
          className="rte-footer-upload-btn"
          // title="上传"
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.05044 1.00098C8.57059 1.00105 8.93598 1.41865 8.93598 1.88652V7.11543H14.1145C14.5842 7.11543 15 7.53122 15 8.00098C14.9999 8.47053 14.5862 8.88649 14.1164 8.88652H8.93598V14.1154C8.93598 14.5851 8.52013 15.0009 8.05044 15.001C7.58068 15.001 7.16489 14.5852 7.16489 14.1154V8.88652H1.88555C1.41584 8.88652 1.00008 8.47067 1 8.00098C1 7.53122 1.41579 7.11543 1.88555 7.11543H7.16489V1.88652C7.16489 1.41677 7.58068 1.00098 8.05044 1.00098Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </Dropdown>
      <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={onPick} />
    </div>
  );
});
UploadButton.displayName = 'UploadButton';
